import { LoadingOutlined } from "@ant-design/icons";
import { useUserActions } from "_actions";
import { InvestmentDataType } from "_interfaces";
import { cx, formatCurrency, formatDate } from "_lib/utils";
import { UserAtom } from "_states";
import { Button, message, Spin } from "antd";
import { TrendingDown, TrendingUp } from "lucide-react";
import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useRecoilState } from "recoil";

export const HomePage = ({ gatewayInstance }: { gatewayInstance: any }) => {
  const [user, setUser] = useRecoilState(UserAtom);
  const [isLoading, setIsLoading] = useState(true);
  const [guest, setGuest] = useState(false);
  const [investments, setInvestments] = useState<InvestmentDataType>();
  const [selectedInvestmentCategory, setSelectedInvestmentCategory] = useState<
    "smallcase" | "equity" | "mutualFunds"
  >("smallcase");
  const userActions = useUserActions();
  const navigate = useNavigate();

  useEffect(() => {
    if (user.scrole === "guest") {
      setGuest(true);
      setIsLoading(false);
    } else {
      userActions
        .getInvestments()
        .then(
          (data) => {
            setInvestments(data.data);
          },
          (error) => {
            console.error(error.response);
            return message.error("Failed to fetch investments");
          }
        )
        .finally(() => {
          setIsLoading(false);
        });
    }
  }, []);

  const connectBroker = () => {
    userActions.connectBroker().then((data) => {
      if (data.error) {
        return message.error("Failed to connect broker");
      }
      const transactionId = data.data.transactionId;

      gatewayInstance
        .triggerTransaction({
          transactionId: transactionId,
        })
        .then((txnResponse: any) => {
          /*
           * Gateway flow ended after order placement
           * Response structure & example - https://developers.gateway.smallcase.com/docs/transaction-response
           */
          console.debug("received response:", txnResponse);
          userActions.connectBrokerComplete(transactionId).then((data) => {
            if (data.data.sctoken) {
              setUser({
                ...user,
                scrole: "user",
                sctoken: data.data.sctoken,
                token: data.data.token,
              });
              localStorage.setItem(
                "user",
                JSON.stringify({
                  ...user,
                  scrole: "user",
                  sctoken: data.data.sctoken,
                  token: data.data.token,
                })
              );
              message.success("Connected to broker successfully");
              navigate(0);
            }
          });
        })
        .catch((err: any) => {
          /*
           * Gateway flow ended before order placement
           * Possible errors - https://developers.gateway.smallcase.com/docs/transaction-errors
           */
          console.error("received error:", err.message);
          message.error(
            "Failed to connect broker. Refresh page to try again!",
            3
          );
        });
    });
    return;
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-[60vh]">
        <Spin indicator={<LoadingOutlined spin />} size="large" />
      </div>
    );
  }
  if (guest) {
    return (
      <div className="flex flex-col items-center justify-center h-[60vh] space-y-8">
        <img
          src={`${process.env.PUBLIC_URL}/logo.svg`}
          alt="Login"
          className="h-28"
        />
        <h3 className="text-lg">Welcome to Capricorne Mindframe</h3>
        <div className="text-center space-y-4">
          <p>Please connect your account with your broker to start investing</p>
          <Button
            className="px-8"
            size="large"
            type="primary"
            onClick={connectBroker}
          >
            Connect Broker
          </Button>
        </div>
      </div>
    );
  } else {
    return (
      <section className="mx-auto space-y-5">
        <div className="grid md:grid-cols-4 items-center bg-white rounded-lg">
          <div className="lg:text-left text-center mx-auto lg:py-10 py-6">
            <h4 className="text-sm">Total Market Value</h4>
            <p className="text-xl font-bold">
              {formatCurrency(investments?.overview.networth)}
            </p>
          </div>
          <div className="lg:text-left text-center mx-auto lg:py-10 py-6">
            <h4 className="text-sm">Total Investment</h4>
            <p className="text-xl font-bold">
              {formatCurrency(investments?.overview.currentInvestment)}
            </p>
          </div>
          <div className="lg:text-left text-center mx-auto lg:py-10 py-6">
            <h4 className="text-sm">Unrealised Returns</h4>
            <p className="flex justify-center items-end text-xl font-bold text-green-500">
              {formatCurrency(investments?.overview.currentReturns)}
              <span className="flex items-center gap-1 text-xs ms-2">
                {(investments?.overview.currentReturnsPercent ?? 0) > 0 ? (
                  <TrendingUp className="w-4" />
                ) : (
                  <TrendingDown className="w-4" />
                )}
                {investments?.overview.currentReturnsPercent}%
              </span>
            </p>
          </div>
          <div className="lg:text-left text-center mx-auto lg:py-10 py-6">
            <h4 className="text-sm">
              Total Returns <span className="text-xs">(incl. div.)</span>
            </h4>
            <p className="flex justify-center items-end text-xl font-bold">
              {formatCurrency(investments?.overview.totalReturns)}
              <span
                className={cx(
                  "flex items-center gap-1 text-xs ms-2",
                  (investments?.overview.totalReturnsPercent ?? 0) > 0
                    ? "text-green-500"
                    : "text-red-500"
                )}
              >
                <TrendingUp className="w-4" />
                {investments?.overview.totalReturnsPercent}%
              </span>
            </p>
          </div>
        </div>

        <div className="flex gap-8 bg-white rounded-lg p-4">
          <button
            className={cx(
              "text-sm font-medium px-3",
              selectedInvestmentCategory === "smallcase"
                ? "font-semibold underline"
                : ""
            )}
            onClick={() => setSelectedInvestmentCategory("smallcase")}
          >
            Smallcase
          </button>
          {/* <a
          className={cx(
            "text-sm font-medium px-3",
            selectedInvestmentCategory == "equity" ? "font-bold underline" : ""
          )}
          onClick={() => setSelectedInvestmentCategory("equity")}
        >
          Equity
        </a>
        <a
          className={cx(
            "text-sm font-medium px-3",
            selectedInvestmentCategory == "mutualFunds"
              ? "font-semibold underline"
              : ""
          )}
          onClick={() => setSelectedInvestmentCategory("mutualFunds")}
        >
          Mutual Funds
        </a> */}
        </div>
        {selectedInvestmentCategory === "smallcase" && (
          <SmallcaseInvestments investments={investments?.smallcase || []} />
        )}
        {selectedInvestmentCategory === "equity" && (
          <p className="text-slate-500 text-center">No investments found</p>
        )}
        {selectedInvestmentCategory === "mutualFunds" && (
          <p className="text-slate-500 text-center">No investments found</p>
        )}
      </section>
    );
  }
};

const SmallcaseInvestments = ({ investments }: { investments: any[] }) => {
  return (
    <>
      {investments.length === 0 && (
        <p className="text-center">
          No investments found.{" "}
          <Link to={"/products"} className="!underline">
            Explore more
          </Link>{" "}
          products
        </p>
      )}
      {investments.map((item, index) => (
        <Link
          key={index}
          to={`/smallcase/${item.investment.iscid}`}
          className="grid md:grid-cols-5 items-center gap-8 bg-white rounded-lg p-4 hover:text-black"
        >
          <div className="flex gap-4 col-span-2 items-center text-sm font-medium px-3">
            <img
              src={item.investment.imageUrl}
              alt={item.investment.name}
              width={80}
              className="w-20 h-20"
            />
            <div>
              <h3 className="text-lg font-semibold pb-2">
                {item.investment.name}
              </h3>
              <p className="text-xs">
                Invested since {formatDate(item.investment.date)}
              </p>
              {/* <p className="text-xs">{item.investment.shortDescription}</p> */}
            </div>
          </div>

          <div className="text-sm font-medium px-3">
            <p>Current Value</p>
            <div className="text-lg font-semibold">
              {formatCurrency(item.investment.returns.insights.currentValue)}
            </div>
          </div>
          <div className="text-sm font-medium px-3">
            <p>Total Returns</p>
            <div
              className={cx(
                "flex justify-start items-end text-lg font-semibold",
                item.investment.returns.insights.totalReturns > 0
                  ? "text-green-500"
                  : "text-red-500"
              )}
            >
              {formatCurrency(item.investment.returns.insights.totalReturns)}
              <span className="flex items-center gap-1 text-xs ms-2">
                {item.investment.returns.insights.totalReturns > 0 ? (
                  <TrendingUp className="w-4" />
                ) : (
                  <TrendingDown className="w-4" />
                )}
                {item.investment.returns.insights.totalReturnsPercent}%
              </span>
            </div>
          </div>

          <div className="text-center">
            <Button type="primary" className="font-bold text-xs px-8">
              View Details
            </Button>
          </div>
        </Link>
      ))}
    </>
  );
};
